import { useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Spinner } from '../../components/UI';

export function LoginPage() {
  const { login, loading, error } = useAuth();

  useEffect(() => {
    // no auto-login
  }, []);

  return (
    <div className="min-h-screen flex" style={{ background: 'linear-gradient(135deg, #0f1c3f 0%, #1B2B6B 40%, #2a4a8a 70%, #1a3060 100%)' }}>
      {/* Left panel — branding */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative overflow-hidden">
        {/* Background decorative shapes */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full opacity-10" style={{ background: '#5B8DB8' }} />
          <div className="absolute bottom-20 -right-10 w-64 h-64 rounded-full opacity-10" style={{ background: '#E05A1C' }} />
          <div className="absolute top-1/2 left-1/3 w-48 h-48 rounded-full opacity-5" style={{ background: '#ffffff' }} />
        </div>

        {/* Logo + brand */}
        <div className="relative z-10">
          <div className="flex items-center gap-4 mb-16">
            <div className="px-4 py-2 rounded-xl" style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)' }}>
              <img src="/logo-honne.png" alt="Honne - Delivering True Value" className="h-16 w-auto" />
            </div>
          </div>

          <div className="space-y-6">
            <h2 className="text-5xl font-bold text-white leading-tight">
              Honne<br />
              <span style={{ color: '#E05A1C' }}>Automated</span><br />
              Reports
            </h2>
            <p className="text-xl text-blue-200 max-w-sm leading-relaxed">
              Plataforma de optimización de costos AWS con análisis automatizado, seguridad y recomendaciones en tiempo real.
            </p>
          </div>
        </div>

        {/* Feature pills */}
        <div className="relative z-10 space-y-3">
          {[
            { icon: '🔍', text: 'Análisis de recursos ociosos y rightsizing' },
            { icon: '🔒', text: 'Auditoría de seguridad y vulnerabilidades' },
            { icon: '📊', text: 'Reportes automáticos el día 10 de cada mes' },
          ].map((f, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.07)', backdropFilter: 'blur(10px)' }}>
              <span className="text-xl">{f.icon}</span>
              <span className="text-sm text-blue-100">{f.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Right panel — login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8" style={{ background: '#f0f4f8' }}>
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-block px-4 py-2 rounded-xl mb-3" style={{ background: 'rgba(27,43,107,0.08)' }}>
              <img src="/logo-honne.png" alt="Honne" className="h-10 w-auto" />
            </div>
            <p className="text-sm" style={{ color: '#5B8DB8' }}>Automated Reports</p>
          </div>

          {/* Card */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Card top accent */}
            <div className="h-1.5 w-full" style={{ background: 'linear-gradient(90deg, #1B2B6B 0%, #5B8DB8 50%, #E05A1C 100%)' }} />

            <div className="p-10">
              {/* Header */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold mb-1" style={{ color: '#1B2B6B' }}>
                  Iniciar Sesión
                </h2>
                <p className="text-sm" style={{ color: '#8A8F9A' }}>
                  Accede con tu cuenta corporativa de Microsoft
                </p>
              </div>

              {error && (
                <div className="mb-6 p-3 rounded-lg border text-sm" style={{ background: '#fff5f5', borderColor: '#fca5a5', color: '#dc2626' }}>
                  {error}
                </div>
              )}

              {/* Microsoft button */}
              <button
                onClick={login}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 font-semibold py-4 px-6 rounded-xl transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  background: loading ? '#d1d5db' : 'linear-gradient(135deg, #1B2B6B 0%, #2a4a8a 100%)',
                  color: 'white',
                  boxShadow: loading ? 'none' : '0 4px 20px rgba(27, 43, 107, 0.4)',
                }}
                onMouseEnter={e => { if (!loading) (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)'; }}
              >
                {loading ? (
                  <Spinner size="sm" />
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                      <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                      <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                      <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
                    </svg>
                    Iniciar sesión con Microsoft
                  </>
                )}
              </button>

              {/* Divider */}
              <div className="flex items-center gap-3 my-6">
                <div className="flex-1 h-px" style={{ background: '#e5e7eb' }} />
                <span className="text-xs" style={{ color: '#9ca3af' }}>acceso corporativo</span>
                <div className="flex-1 h-px" style={{ background: '#e5e7eb' }} />
              </div>

              {/* Info */}
              <div className="p-4 rounded-xl text-center" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <p className="text-xs" style={{ color: '#64748b' }}>
                  Solo para empleados de Honne.<br />
                  Usa tu cuenta <span className="font-semibold" style={{ color: '#1B2B6B' }}>@honne.com</span>
                </p>
              </div>
            </div>
          </div>

          <p className="text-center text-xs mt-6" style={{ color: '#94a3b8' }}>
            © {new Date().getFullYear()} Honne · Cloud Cost Optimization Platform
          </p>
        </div>
      </div>
    </div>
  );
}
